import { SERVICE_DOMAIN, SERVICE_ENV } from '@/libs/constant';
import { filter, pipe, split, takeUntil, toArray } from '@fxts/core';
import { CERTIFICATION_001 } from '../../Certification/error';
import { LOGIN_002, SIGNIN_001, SIGNUP_001, TOKEN_001, USER_002, USER_ADDRESS_001 } from '../error';
import {
  AdminProfileChangeControllerReqBody,
  ChangeEmailControllerReqBody,
  ChangePwControllerReqBody,
  CreateTokenControllerReqBody,
  ProfileChangeControllerReqBody,
  SigninControllerReqBody,
  SignupControllerReqBody,
  SignupEmailAndPasswordControllerReqBody,
  SnsDisconnectControllerReqBody,
  SnsSignupControllerReqBody,
  SyncSigninControllerReqBody,
  UserAbroadAddressMutator,
  UserAddressMutator,
} from '../schema';
import type {
  AdminProfileChangeControllerReqBodyType,
  AgreementControllerReqBodyType,
  ChangeEmailControllerReqBodyType,
  ChangeUserAbroadAddressControllerReqBodyType,
  ChangeUserAddressControllerReqBodyType,
  CreateTokenControllerReqQueryType,
  ProfileChangeControllerReqBodyType,
  SignupControllerReqBodyType,
  SnsDisconnectControllerReqBodyType,
  SnsSignupControllerReqBodyType,
  SyncSigninControllerReqBodyType,
} from '../type';
import { checkEmailChar } from './checkEmailChar';

export const validateRedirectUrl = ({ redirect_url, client_host }: { redirect_url: string; client_host: string }) => {
  if (SERVICE_ENV === 'development') {
    return redirect_url;
  }

  try {
    const redirect_url_obj = new URL(decodeURIComponent(redirect_url));

    const is_valid_redirect_url = pipe(
      client_host,
      split(','),
      filter((host) => {
        if (redirect_url_obj.hostname.includes(SERVICE_DOMAIN)) {
          return true;
        }

        const host_url = new URL(host);
        return redirect_url_obj.hostname.includes(host_url.hostname);
      }),
      takeUntil((is_included) => is_included),
      toArray,
    );

    if (!is_valid_redirect_url.length) {
      throw LOGIN_002();
    }
  } catch (e) {
    throw LOGIN_002(e instanceof Error ? e : undefined);
  }

  return redirect_url;
};

export const validateSnsSignupReqBody = (body: SnsSignupControllerReqBodyType) => {
  const parse_body = SnsSignupControllerReqBody.safeParse(body);

  if (!parse_body.success) {
    throw SIGNUP_001(parse_body.error);
  }

  return parse_body.data;
};

export const validateAgreementReqBody = (body: AgreementControllerReqBodyType) => {
  const parse_body = SnsSignupControllerReqBody.safeParse(body);

  if (!parse_body.success) {
    throw SIGNUP_001(parse_body.error);
  }

  return parse_body.data;
};

export const validateSignupReqBody = (body: Record<string, any>) => {
  const parse_body = SignupControllerReqBody.safeParse(body);

  if (!parse_body.success) {
    throw SIGNUP_001(parse_body.error);
  }

  if (!checkEmailChar(parse_body.data.password, parse_body.data.email)) {
    throw SIGNUP_001();
  }

  return parse_body.data;
};

export const validateSignupEmailAndPasswordReqBody = (
  body: Pick<SignupControllerReqBodyType, 'email' | 'password' | 'is_agree_notification'>,
) => {
  const parse_body = SignupEmailAndPasswordControllerReqBody.safeParse(body);

  if (!parse_body.success) {
    throw SIGNUP_001(parse_body.error);
  }

  if (!checkEmailChar(parse_body.data.password, parse_body.data.email)) {
    throw SIGNUP_001();
  }

  return parse_body.data;
};

export const validateSigninReqBody = (body: Record<string, any>) => {
  const parse_body = SigninControllerReqBody.safeParse(body);

  if (!parse_body.success) {
    throw SIGNIN_001(parse_body.error);
  }

  return parse_body.data;
};

export const validateCreateTokenReqBody = (body: CreateTokenControllerReqQueryType) => {
  const parse_body = CreateTokenControllerReqBody.safeParse(body);

  if (!parse_body.success) {
    throw TOKEN_001(parse_body.error);
  }

  return parse_body.data;
};

export const validateChangeProfileReqBody = (data: ProfileChangeControllerReqBodyType) => {
  const parse_body = ProfileChangeControllerReqBody.safeParse(data);

  if (!parse_body.success) {
    throw USER_002(parse_body.error);
  }

  return parse_body.data;
};

export const validationResetPwReqBody = (data: Record<string, any> & { email: string }) => {
  const parse_body = ChangePwControllerReqBody.safeParse(data);

  if (!parse_body.success) {
    throw USER_002(parse_body.error);
  }

  if (!checkEmailChar(parse_body.data.password, data.email)) {
    throw SIGNUP_001();
  }

  return parse_body.data;
};

export const validationChangeEmailReqBody = (data: ChangeEmailControllerReqBodyType) => {
  const parse_body = ChangeEmailControllerReqBody.safeParse(data);

  if (!parse_body.success) {
    throw CERTIFICATION_001(parse_body.error);
  }

  return parse_body.data;
};

export const validationSnsDisconnectReqBody = (data: SnsDisconnectControllerReqBodyType) => {
  const parse_body = SnsDisconnectControllerReqBody.safeParse(data);

  if (!parse_body.success) {
    throw CERTIFICATION_001(parse_body.error);
  }

  return parse_body.data;
};

export const validateSyncSigninReqBody = (body: SyncSigninControllerReqBodyType) => {
  const parse_body = SyncSigninControllerReqBody.safeParse(body);

  if (!parse_body.success) {
    throw SIGNIN_001(parse_body.error);
  }

  return parse_body.data;
};

export const validateUpsertUserAddressReqBody = (body: ChangeUserAddressControllerReqBodyType) => {
  const parse_body = UserAddressMutator.safeParse(body);

  if (!parse_body.success) {
    throw USER_ADDRESS_001(parse_body.error);
  }

  return parse_body.data;
};

export const validateUpsertUserAbroadAddressReqBody = (body: ChangeUserAbroadAddressControllerReqBodyType) => {
  const parse_body = UserAbroadAddressMutator.safeParse(body);

  if (!parse_body.success) {
    throw USER_ADDRESS_001(parse_body.error);
  }

  return parse_body.data;
};

export const validateAdminChangeProfileReqBody = (data: AdminProfileChangeControllerReqBodyType) => {
  const parse_body = AdminProfileChangeControllerReqBody.safeParse(data);

  if (!parse_body.success) {
    throw USER_002(parse_body.error);
  }

  return parse_body.data;
};
